import { useCallback } from 'react';
import {
  SearchSelectField,
  type SearchSelectOption,
  type SearchSelectPage,
} from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';
import type { CmsPage, EventAdminView } from '../types/cms';

const BASE = '/admin/v1/events';
const PAGE_SIZE = 20;

// 라벨 정책: raw UUID/ULID 미노출. title 우선, 없으면 eventKey (사람이 읽는 slug),
// 둘 다 없을 때만 '제목 없음' placeholder + hint 에 eventKey 노출.
const toOption = (e: EventAdminView): SearchSelectOption => {
  const label = e.title?.trim() || e.eventKey?.trim() || '제목 없음';
  return {
    value: e.eventId,
    label,
    hint: [e.status, e.startDate, e.endDate].filter(Boolean).join(' · ') || undefined,
    thumbnailUrl: e.thumbnailUrl || null,
  };
};

// BE `/admin/v1/events` 는 keyword param 미지원 → 서버 page 호출 + 클라 필터.
// Backlog: BE 에 `q` 또는 `keyword` 추가 시 server-side 필터로 전환.
const fetchEvents = async (keyword: string, page: number): Promise<SearchSelectPage> => {
  const qs = new URLSearchParams({
    page: String(page),
    size: String(PAGE_SIZE),
    sort: 'sortOrder,asc',
  });
  const res = await adminFetch(`${BASE}?${qs.toString()}`);
  if (!res.ok) throw new Error(`이벤트 검색 실패 (${res.status})`);
  const env = (await res.json()) as {
    data?: EventAdminView[] | CmsPage<EventAdminView>;
  };
  const raw = env.data;
  const all = Array.isArray(raw) ? raw : (raw?.content ?? []);
  const totalElements = Array.isArray(raw)
    ? raw.length
    : (raw?.totalElements ?? all.length);

  const trimmed = keyword.trim().toLowerCase();
  const filtered = trimmed
    ? all.filter((e) => {
        const haystack = [e.title, e.eventKey, e.description ?? '']
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(trimmed);
      })
    : all;

  const items = filtered.map(toOption);
  // hasMore — 서버 page 가 가득 차면 다음 page 가능. 클라 필터 결과 0 이어도
  // 다음 페이지에 매칭 있을 수 있으므로 서버 raw 기준 판단.
  const hasMore = Array.isArray(raw)
    ? false
    : (page + 1) * PAGE_SIZE < totalElements;
  return { items, hasMore, total: totalElements };
};

const fetchEvent = async (eventId: string): Promise<SearchSelectOption | null> => {
  const res = await adminFetch(`${BASE}/${eventId}`);
  if (!res.ok) return null;
  const env = (await res.json()) as { data?: EventAdminView };
  if (!env.data) return null;
  return toOption(env.data);
};

interface EventSearchSelectProps {
  value: string | null;
  onChange: (eventId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  testId?: string;
  ariaLabel?: string;
}

export const EventSearchSelect = ({
  value,
  onChange,
  placeholder = '이벤트명으로 검색',
  disabled,
  errorMessage,
  testId,
  ariaLabel,
}: EventSearchSelectProps) => {
  const loadOptions = useCallback((q: string, page: number) => fetchEvents(q, page), []);
  const resolveOption = useCallback((v: string) => fetchEvent(v), []);

  return (
    <SearchSelectField
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      resolveOption={resolveOption}
      placeholder={placeholder}
      emptyMessage="검색 결과가 없습니다"
      loadingMessage="검색 중..."
      {...(disabled !== undefined ? { disabled } : {})}
      {...(errorMessage !== undefined && errorMessage !== null ? { errorMessage } : {})}
      {...(testId !== undefined ? { testId } : {})}
      {...(ariaLabel !== undefined ? { ariaLabel } : {})}
    />
  );
};
